import React, { useEffect, useState } from 'react';
import { Button, Container, Typography, Box, Grid2 } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CustomInput from '../../theme/style-components/CustomInput';
import { useEditEmployeeMyEmpObjMutation, useGetEmployeeMyEmpObjQuery } from '../../store/employee/EmployeeApiSlice';
import CustomButton from '../../theme/style-components/CustomButton';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";

const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    office_location: yup.string().required('Office location is required'),
    phone: yup.string().required('Phone is required'),
    image_url: yup.string().url('Enter a valid URL'),
    resume_url: yup.string().url('Enter a valid URL'),
    linkedin_url: yup.string().url('Enter a valid URL'),
    job_description: yup
        .string()
        .required("Job Description is required")
        .max(300, "Maximum 300 characters allowed"),
    job_description_url: yup.string().url('Enter a valid URL'),
});

const EditProfile = () => {

    const navigate = useNavigate();
    const [editEmployeeProfile] = useEditEmployeeMyEmpObjMutation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    const {
        isLoading,
        data: employeeData,
        error,
        refetch: refetchEmployee,
    } = useGetEmployeeMyEmpObjQuery();

    const [initialValues, setInitialValues] = useState({
        name: '',
        office_location: '',
        phone: '',
        image: '',
        resume_url: '',
        linkedin_url: '',
        job_description: '',
        job_description_url: ''
    });

    useEffect(() => {
        if (employeeData?.data) {
            const data = employeeData.data;
            setInitialValues(data);
            formik.setValues(data);
    
            // Check if the image field exists in the response
            if (data?.image) {
                const baseUrl = process.env.REACT_APP_MEDIA_URL || 'http://localhost:8000';
                const fullImageUrl = `${baseUrl}${data.image}`;
                setPreviewUrl(fullImageUrl);
            }
        }
    }, [employeeData]);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        // Check if image_url is already set
        if (formik.values.image_url && formik.values.image_url.trim() !== '') {
            toast.error('Please use either image upload or image URL, not both');
            event.target.value = ''; // Reset the input
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
            event.target.value = ''; 
            return;
        }
        
        // Check file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            toast.error('File size should be less than 5MB');
            event.target.value = ''; // Reset the input
            return;
        }

        setSelectedFile(file);
        
        // Create preview URL for display only
        const imageUrl = URL.createObjectURL(file);
        setPreviewUrl(imageUrl);
        formik.setFieldValue('image_url', ''); // Clear the image_url text input if a file is selected
    };

    const handleCancelImage = () => {
        setSelectedFile(null);
        setPreviewUrl('');
        formik.setFieldValue('image', '');
        formik.setFieldValue('image_url', '');
        // Reset file input
        const fileInput = document.getElementById('image-upload');
        if (fileInput) fileInput.value = '';
    };

    const handleImageUrlChange = (e) => {
        const url = e.target.value;
        
        // Check if a file is already selected
        if (selectedFile) {
            toast.error('Please use either image upload or image URL, not both');
            e.target.value = ''; // Reset the input
            return;
        }

        formik.handleChange(e); // Update formik value
        if (url && url.trim() !== '') {
            setPreviewUrl(url);
            setSelectedFile(null); // Clear selected file when URL is provided
            // Reset file input
            const fileInput = document.getElementById('image-upload');
            if (fileInput) fileInput.value = '';
            // Set image to empty string when URL is provided
            formik.setFieldValue('image', '');
        } else {
            setPreviewUrl('');
            formik.setFieldValue('image', '');
            formik.setFieldValue('image_url', '');
        }
    };

    // Cleanup preview URL when component unmounts
    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const formik = useFormik({
        validationSchema,
        enableReinitialize: true,
        initialValues,
        onSubmit: async (values) => {
            try {
                const formData = new FormData();
                
                // Add all text fields
                formData.append('name', values.name);
                formData.append('office_location', values.office_location);
                formData.append('phone', values.phone);
                formData.append('resume_url', values.resume_url || '');
                formData.append('linkedin_url', values.linkedin_url || '');
                formData.append('job_description', values.job_description);
                formData.append('job_description_url', values.job_description_url || '');

                // Handle image data
                if (values.image_url && values.image_url.trim() !== '') {
                    // If URL exists, use that as profile image
                    formData.append('image_url', values.image_url);
                    formData.append('image', ''); // Set image to empty string when URL is provided
                } else if (selectedFile) {
                    // Only use file if no URL is provided
                    formData.append('image', selectedFile);
                    formData.append('image_url', ''); // Clear URL if file exists
                } else {
                    // If neither file nor URL is provided
                    formData.append('image', '');
                    formData.append('image_url', '');
                }

                const result = await editEmployeeProfile(formData).unwrap();
                toast.success("Employee profile updated successfully");
                refetchEmployee();
                navigate("/");
            } catch (error) {
                console.error('Error updating profile:', error);
                toast.error(error?.data?.message || "Failed to update profile");
            }
        },
    });

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error occurred: {error.message}</div>;

    return (
        <>
            <form>
            <Grid2 container spacing={2} sx={{ mt: 2 }}>
                    <Grid2 xs={12}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px", mb: 2 }}>
                                Profile Image
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {previewUrl && (
                                    <Box
                                        component="img"
                                        src={previewUrl}
                                        alt="Profile preview"
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                            border: '1px solid #ccc'
                                        }}
                                    />
                                )}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <input
                                        type="file"
                                        id="image-upload"
                                        style={{ display: 'none' }}
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                    />
                                    <CustomButton
                                        color="primary"
                                        type="button"
                                        buttonName={selectedFile ? "Change Image" : "Upload Image"}
                                        onClick={() => document.getElementById('image-upload').click()}
                                    />
                                    {(selectedFile || previewUrl) && (
                                        <CustomButton
                                            color="error"
                                            type="button"
                                            buttonName="Cancel"
                                            onClick={handleCancelImage}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Grid2>
                </Grid2>
                <Grid2 container spacing={2} marginTop={5}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Name
                            </Typography>
                            <CustomInput
                                id="name"
                                name="name"
                                placeholder={"Enter name"}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Office location
                            </Typography>
                            <CustomInput
                                id="office_location"
                                name="office_location"
                                placeholder={"Enter office location"}
                                value={formik.values.office_location}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.office_location &&
                                    Boolean(formik.errors.office_location)
                                }
                                helperText={
                                    formik.touched.office_location &&
                                    formik.errors.office_location
                                }
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                </Grid2>
                <Grid2 container spacing={2} sx={{mt:2}}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Phone
                            </Typography>
                            <CustomInput
                                id="phone"
                                name="phone"
                                placeholder={"Enter phone number"}
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                error={formik.touched.phone && Boolean(formik.errors.phone)}
                                helperText={formik.touched.phone && formik.errors.phone}
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Image url
                            </Typography>
                            <CustomInput
                                id="image_url"
                                name="image_url"
                                placeholder={"Enter image url"}
                                value={formik.values.image_url}
                                onChange={handleImageUrlChange}
                                error={formik.touched.image_url && Boolean(formik.errors.image_url)}
                                helperText={formik.touched.image_url && formik.errors.image_url}
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                </Grid2>
                <Grid2 container spacing={2} sx={{mt:2}}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Resume url
                            </Typography>
                            <CustomInput
                                id="resume_url"
                                name="resume_url"
                                placeholder={"Enter resume url"}
                                value={formik.values.resume_url}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.resume_url && Boolean(formik.errors.resume_url)
                                }
                                helperText={
                                    formik.touched.resume_url && formik.errors.resume_url
                                }
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Linkedin url
                            </Typography>
                            <CustomInput
                                id="linkedin_url"
                                name="linkedin_url"
                                placeholder={"Enter linkedin url"}
                                value={formik.values.linkedin_url}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.linkedin_url &&
                                    Boolean(formik.errors.linkedin_url)
                                }
                                helperText={
                                    formik.touched.linkedin_url && formik.errors.linkedin_url
                                }
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                </Grid2>
                <Grid2 container spacing={2} sx={{mt:2}}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Job description
                            </Typography>
                            <CustomInput
                                id="job_description"
                                name="job_description"
                                placeholder={"Enter job description"}
                                value={formik.values.job_description}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.job_description &&
                                    Boolean(formik.errors.job_description)
                                }
                                helperText={
                                    formik.touched.job_description &&
                                    formik.errors.job_description
                                }
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: "15px" }}>
                                Job description url
                            </Typography>
                            <CustomInput
                                id="job_description_url"
                                name="job_description_url"
                                placeholder={"Enter job description url"}
                                value={formik.values.job_description_url}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.job_description_url &&
                                    Boolean(formik.errors.job_description_url)
                                }
                                helperText={
                                    formik.touched.job_description_url &&
                                    formik.errors.job_description_url
                                }
                                sx={{ maxWidth: 50 }}
                            />
                        </Box>
                    </Grid2>
                </Grid2>
                <Grid2 size={{ xs: 12 }} sx={{mt:5}}>
                    <Box display={"flex"} justifyContent={"end"} gap={1.5}>
                        <CustomButton
                            color="primary"
                            type="button"
                            buttonName="Cancel"
                            onClick={() => navigate("/employee")}
                        />
                        <CustomButton
                            color="primary"
                            type="button"
                            buttonName="Submit"
                            onClick={() => formik.handleSubmit()}
                        />
                    </Box>
                </Grid2>
            </form>
        </>
    );
};

export default EditProfile;